import { useEffect, useState } from 'react';
import { useDialogA11y } from '../lib/use-dialog-a11y.js';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  Box,
  CalendarClock,
  ClipboardList,
  Inbox,
  LayoutDashboard,
  MoreHorizontal,
  Receipt,
  Settings,
  Truck,
  UserRound,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../lib/cn.js';
import { useAuth } from '../lib/auth-store.js';
import type { AuthUser } from '../lib/auth-store.js';

interface TabDef {
  to: string;
  /** Path prefix used to highlight the tab (so /inventory/$id keeps Inventory active). */
  matchPrefix: string;
  label: string;
  Icon: LucideIcon;
  /** Which roles see this tab. Undefined = visible to everyone. */
  roles?: ReadonlyArray<AuthUser['role']>;
}

/** Primary mobile tabs — always one tap away. */
const PRIMARY_TABS: TabDef[] = [
  { to: '/inventory', matchPrefix: '/inventory', label: 'Inventory', Icon: Box },
  { to: '/quote-builder', matchPrefix: '/quote-builder', label: 'Quote', Icon: Receipt },
  { to: '/jobs', matchPrefix: '/jobs', label: 'Jobs', Icon: CalendarClock },
  { to: '/me', matchPrefix: '/me', label: 'Me', Icon: UserRound },
];

/** Everything else — surfaced via the More sheet on mobile so we don't drop
 * destinations that exist on the desktop side rail. /sales-orders folded
 * into /operations; the search inside Operations covers the old "find an
 * order" lookup.
 *
 * Field techs see only the 4 primary tabs (Inventory · Quote · Jobs · Me)
 * — the More sheet is hidden for that role since none of these
 * destinations apply to their workflow. */
const OFFICE_ROLES: ReadonlyArray<AuthUser['role']> = ['admin', 'dispatcher', 'office'];
const ADMIN_ONLY: ReadonlyArray<AuthUser['role']> = ['admin'];
const MORE_TABS: TabDef[] = [
  { to: '/', matchPrefix: '/', label: 'Today', Icon: LayoutDashboard, roles: OFFICE_ROLES },
  { to: '/customers', matchPrefix: '/customers', label: 'Customers', Icon: Users, roles: OFFICE_ROLES },
  { to: '/operations', matchPrefix: '/operations', label: 'Operations', Icon: ClipboardList, roles: OFFICE_ROLES },
  { to: '/trucks', matchPrefix: '/trucks', label: 'Trucks', Icon: Truck, roles: OFFICE_ROLES },
  // /contracts is the Phase 6 stub — hidden from nav until the surface ships.
  { to: '/outbox', matchPrefix: '/outbox', label: 'Outbox', Icon: Inbox },
  { to: '/admin', matchPrefix: '/admin', label: 'Admin', Icon: Settings, roles: ADMIN_ONLY },
];

function visibleFor(role: AuthUser['role'] | null, tab: TabDef): boolean {
  if (!tab.roles) return true;
  return role !== null && tab.roles.includes(role);
}

/**
 * Mobile-only bottom tab bar. Hidden at md and up — desktop uses the side
 * navigation in the root layout.
 *
 * Active state matches by path prefix so deep routes (e.g. `/inventory/$id`)
 * keep the Inventory tab highlighted. Matches the .nav strip in
 * Design/hvac_mobile_inventory_quote_signature.html.
 *
 * The 5th slot is a "More" sheet that surfaces the desktop-only routes
 * (Customers, Orders, Dispatch, Contracts, Outbox, Admin) so mobile users
 * aren't trapped on the 4 primary destinations.
 */
export function BottomTabNav() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const role = useAuth((s) => s.user?.role ?? null);
  const [sheetOpen, setSheetOpen] = useState(false);
  // Inline-conditional sheet ({sheetOpen && …}) — enabled flag keeps the
  // Esc listener + focus trap attached only while the sheet is up.
  const sheetRef = useDialogA11y<HTMLDivElement>(() => setSheetOpen(false), sheetOpen);

  // Close the sheet on navigation.
  useEffect(() => {
    setSheetOpen(false);
  }, [path]);

  // Filter both rails by role. Field techs end up with the 4 primary tabs
  // only — the More button is suppressed when nothing in MORE_TABS is
  // visible.
  const visibleMore = MORE_TABS.filter((tab) => visibleFor(role, tab));
  const showMore = visibleMore.length > 0;

  // Highlight "More" when the current route is one of the visible MORE_TABS.
  const moreActive = visibleMore.some(({ matchPrefix }) =>
    matchPrefix === '/' ? path === '/' : path === matchPrefix || path.startsWith(`${matchPrefix}/`),
  );

  return (
    <>
      <nav
        aria-label="Primary"
        className={cn(
          'md:hidden fixed inset-x-0 bottom-0 z-30',
          'border-t bg-surface',
          'pb-[env(safe-area-inset-bottom)]',
        )}
        style={{ borderColor: 'var(--color-border-tertiary)' }}
      >
        <ul className={cn('grid', showMore ? 'grid-cols-5' : 'grid-cols-4')}>
          {PRIMARY_TABS.map(({ to, matchPrefix, label, Icon }) => {
            const active =
              path === matchPrefix || path.startsWith(`${matchPrefix}/`);
            return (
              <li key={to}>
                <Link
                  to={to}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex flex-col items-center gap-0.5 py-2 text-[11px] font-medium transition-colors',
                    active ? 'text-ink-accent' : 'text-ink-muted',
                  )}
                >
                  <Icon className="h-[22px] w-[22px]" aria-hidden />
                  <span>{label}</span>
                </Link>
              </li>
            );
          })}
          {showMore && (
            <li>
              <button
                type="button"
                onClick={() => setSheetOpen((v) => !v)}
                aria-expanded={sheetOpen}
                aria-controls="more-sheet"
                className={cn(
                  'w-full flex flex-col items-center gap-0.5 py-2 text-[11px] font-medium transition-colors',
                  moreActive || sheetOpen ? 'text-ink-accent' : 'text-ink-muted',
                )}
              >
                <MoreHorizontal className="h-[22px] w-[22px]" aria-hidden />
                <span>More</span>
              </button>
            </li>
          )}
        </ul>
      </nav>

      {sheetOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="More destinations"
          id="more-sheet"
          className="md:hidden fixed inset-0 z-40 flex items-end bg-slate-950/40"
          onClick={() => setSheetOpen(false)}
        >
          <div
            ref={sheetRef}
            onClick={(e) => e.stopPropagation()}
            className="w-full rounded-t-xl border-t pb-[env(safe-area-inset-bottom)]"
            style={{
              borderColor: 'var(--color-border-tertiary)',
              backgroundColor: 'var(--color-elevated-surface)',
            }}
          >
            <header className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--color-border-tertiary)' }}>
              <span className="text-sm font-medium text-ink">More destinations</span>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                aria-label="Close more sheet"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted hover:text-ink"
              >
                <X className="h-4 w-4" />
              </button>
            </header>
            <ul className="grid grid-cols-3 gap-1 p-3">
              {visibleMore.map(({ to, matchPrefix, label, Icon }) => {
                const active =
                  matchPrefix === '/'
                    ? path === '/'
                    : path === matchPrefix || path.startsWith(`${matchPrefix}/`);
                return (
                  <li key={to}>
                    <Link
                      to={to}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'flex flex-col items-center gap-1 rounded-md py-3 text-xs font-medium transition-colors',
                        active ? 'bg-brand-50 text-ink-accent' : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
                      )}
                    >
                      <Icon className="h-5 w-5" aria-hidden />
                      <span>{label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
